import React, { useMemo } from 'react'
import { EmsProductTypeValue } from 'src/Routes/Installation/InstallationModel'
import { makeStyles } from 'tss-react/mui'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getEmsProductImage } from 'src/Libs/installations/utils/product-image'
import { isUndefined } from 'lodash'
import { ImageVersions } from 'src/Libs/installations/types'
import { useActiveInstallationContext } from 'src/Libs/installations'
import { FarmIcon } from '@e3dc-react/icons'

type CardVariants = 'small' | 'large'

interface StyleProps {
  variant: CardVariants
  isSelected: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    display: 'flex',
    overflow: 'hidden',
    flexWrap: 'nowrap',
    flexDirection: props.variant === 'large' ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: props.variant === 'large' ? 'center' : 'start',
    height: '100%',
    width: '100%',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    border: '1px solid',
    borderColor: props.isSelected ? theme.palette.primary.main : theme.palette.grey.grey06,
    background: theme.palette.background.paper2,
    borderRadius: theme.shape.borderRadius,
    '@media (hover: hover)': {
      '&:hover': {
        transition: theme.transitions.create('all'),
        transform: 'scale(1.01)',
        cursor: 'pointer',
      },
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  textWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  storageId: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  farmImgContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: 'auto',
  },
}))

interface Props {
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  storageId: string
  isClient: boolean
  variant: CardVariants
  isSelected: boolean
  productType?: EmsProductTypeValue
}

const FARM_ICON_SIZE = 48
const STORAGE_ICON_SIZE_LARGE = 90
const STORAGE_ICON_SIZE_SMALL = 48

/**
 * Displays a farm member which can be selected by the user
 */
const FarmSelectionCard: React.FC<Props> = ({ onClick, storageId, isClient, variant, isSelected, productType }) => {
  const { classes } = useStyles({ variant, isSelected })
  const { t } = useCustomTranslation()
  const { installation } = useActiveInstallationContext()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const storageIconSize = variant === 'large' ? STORAGE_ICON_SIZE_LARGE : STORAGE_ICON_SIZE_SMALL

  const productImage = useMemo<string | undefined>(() => {
    if (isUndefined(productType)) return undefined
    return getEmsProductImage(
      productType,
      installation?.installationStatus,
      variant === 'large' ? ImageVersions.ThreeDimensional : ImageVersions.TwoDimensional
    )
  }, [productType, variant, installation?.installationStatus])

  return (
    <Box onClick={onClick} className={classes.container} data-qa={`farm-selection-${storageId}`}>
      <Box className={classes.textWrapper}>
        <Typography variant={downSm ? 'subtitle2' : 'h3'} color="primary" className={classes.storageId}>
          {t('framework.installation.serialnumberShort', { serialnumber: storageId })}
        </Typography>
        <Typography variant="caption" alignSelf={'auto'} className={classes.storageId}>
          {isClient ? t('farm.client') : t('farm.master')}
        </Typography>
      </Box>

      <Box className={classes.farmImgContainer}>
        {!isClient && (
          <Box sx={{ height: FARM_ICON_SIZE, width: FARM_ICON_SIZE, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <FarmIcon />
          </Box>
        )}
        {productImage && isClient && (
          <img src={productImage} alt="ems master product" height={storageIconSize} width={storageIconSize} style={{ display: 'block' }} />
        )}
      </Box>
    </Box>
  )
}

export default FarmSelectionCard
